import {
  Badge,
  Button,
  IconBadge,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { PencilIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  getCategoryDisplayLabel,
  getSpringboardTabById,
  type SpringboardCustomizationFormCategory,
} from '@/features/springboard';
import { TOOL_BADGE_TINTS } from '@/shared/ui/ToolBadge';

/** Shared with the skeleton, so the swap to loaded content does not shift the grid. */
const CARD_CLASS = 'flex min-w-0 flex-col gap-2xl rounded-lg border border-border-light p-lg';

interface CategoryCardProps {
  /** The stored overrides; an empty field reads as the category's default copy. */
  category: SpringboardCustomizationFormCategory;
  onEdit: () => void;
}

export function CategoryCard({ category, onEdit }: CategoryCardProps) {
  const { t } = useTranslation('admin');
  const defaults = getSpringboardTabById(category.id);

  const label = getCategoryDisplayLabel(category.id, category.label);
  const subtitle = category.subtitle || defaults?.subtitle;
  const description = category.description || defaults?.description;
  const isCustomized = Boolean(category.label || category.subtitle || category.description);
  const editLabel = t('admin.customization.editCategory');

  return (
    <article data-testid={`category-card-${category.id}`} className={CARD_CLASS}>
      <div className="flex items-center gap-xs">
        <IconBadge className={`shrink-0 ${TOOL_BADGE_TINTS[category.id]}`}>
          {defaults && <defaults.Icon />}
        </IconBadge>
        {isCustomized && (
          <Badge variant="secondary" size="sm">
            {t('admin.customization.customized')}
          </Badge>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              className="ml-auto"
              aria-label={editLabel}
              onClick={onEdit}
            >
              <PencilIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{editLabel}</TooltipContent>
        </Tooltip>
      </div>

      <dl className="flex flex-col gap-sm">
        <div className="flex flex-col gap-2xs">
          <dt className="type-label-xs text-foreground-subtle">{t('admin.customization.label')}</dt>
          <dd>
            <h2 className="type-body-xl-strong truncate text-foreground">{label}</h2>
          </dd>
        </div>
        <div className="flex flex-col gap-2xs">
          <dt className="type-label-xs text-foreground-subtle">
            {t('admin.customization.subtitle')}
          </dt>
          <dd className="type-body-md text-foreground">{subtitle}</dd>
        </div>
        <div className="flex flex-col gap-2xs">
          <dt className="type-label-xs text-foreground-subtle">
            {t('admin.customization.description')}
          </dt>
          <dd className="type-body-md text-foreground">{description}</dd>
        </div>
      </dl>
    </article>
  );
}

/** Mirrors `CategoryCard`'s layout. */
export function CategoryCardSkeleton() {
  return (
    <div data-testid="category-card-skeleton" aria-hidden className={CARD_CLASS}>
      <div className="flex items-center gap-xs">
        <Skeleton className="size-10 shrink-0 rounded-sm" />
        <Skeleton className="ml-auto size-8 rounded-sm" />
      </div>

      <div className="flex flex-col gap-sm">
        <div className="flex flex-col gap-2xs">
          <Skeleton className="h-3 w-12" />
          <Skeleton className="h-6 w-2/5" />
        </div>
        <div className="flex flex-col gap-2xs">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-4 w-3/5" />
        </div>
        <div className="flex flex-col gap-2xs">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-4/5" />
        </div>
      </div>
    </div>
  );
}
